import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';

import { useSseConnection } from 'core/hooks';
import { NOTIFICATIONS_QUERY_KEY } from 'notifications/constants';
import { ISseOrderEvent, ISseOrderStatusData } from 'orders/types';
import { parseOrderSseEvent } from 'orders/utils';
import { ExternalOrderStatusType, QuoteStatus } from 'quotes/enums';
import { IBaseQuote } from 'quotes/types';

const ORDER_STATUS_SSE_PATH = '/api/sse/order-status';

interface IOrderStatusSnapshot {
  externalStatus: ExternalOrderStatusType | null;
  status: QuoteStatus | undefined;
}

interface IProps {
  order: IBaseQuote | undefined;
  enabled?: boolean;
}

export const useExternalOrderStatus = ({ order, enabled = true }: IProps) => {
  const [sseStatusData, setSseStatusData] = useState<ISseOrderStatusData | null>(null);
  const lastStatusRef = useRef<IOrderStatusSnapshot | null>(null);

  const queryClient = useQueryClient();

  // Seeded from the loaded order, so a connect-time snapshot of the same status is no change.
  useEffect(() => {
    lastStatusRef.current = order?.id
      ? { status: order?.status, externalStatus: order?.externalStatus ?? null }
      : null;
  }, [order?.id, order?.status, order?.externalStatus]);

  const onMessage = useCallback(
    (event: ISseOrderEvent) => {
      const data = parseOrderSseEvent(event);

      if (!data) {
        console.error('[SSE] Could not parse order status event');

        return;
      }

      const { status, externalStatus } = data.order;
      const previous = lastStatusRef.current;

      lastStatusRef.current = { status, externalStatus };
      setSseStatusData(data);

      // The BE raises the bell notification for this change; without this it would only
      // show up on the next summary poll.
      if (previous?.status !== status || previous?.externalStatus !== externalStatus) {
        queryClient.invalidateQueries([NOTIFICATIONS_QUERY_KEY]);
      }
    },
    [queryClient]
  );

  const sseUrl = useMemo(
    () => (order?.id ? `${ORDER_STATUS_SSE_PATH}?order_id=${order.id}` : undefined),
    [order?.id]
  );

  useSseConnection<ISseOrderEvent>({
    url: sseUrl,
    enabled,
    eventName: 'order-status-update',
    onMessage,
  });

  return { sseStatusData };
};
